import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { store, RootState } from './store';
import { lightTheme, darkTheme } from './styles/themes';

// Components
import Layout from './components/common/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ConfirmProvider } from './components/common/ConfirmDialog';
import InstallPrompt from './components/common/InstallPrompt';
import OnboardingTour from './components/common/OnboardingTour';
import PwaUpdatePrompt from './components/common/PwaUpdatePrompt';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import LegalAcceptanceGate from './components/common/LegalAcceptanceGate';
import { TabsProvider } from './contexts/TabsContext';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import DashboardLayout from './components/dashboard/DashboardLayout';
import TradesTable from './components/trades/TradesTable';

import AlertsList from './components/alerts/AlertsList';
import UserSettings from './components/settings/UserSettings';
import LogsPage from './pages/LogsPage';
import InstrumentsPage from './pages/InstrumentsPage';
import ZerodhaCallbackPage from './pages/ZerodhaCallbackPage';
import OptionsDashboardPage from './pages/OptionsDashboardPage';
import OptionsLabPage from './pages/OptionsLabPage';
import OptionsHubPage from './pages/OptionsHubPage';
import StrategyHubPage from './pages/StrategyHubPage';
import AiDeskPage from './pages/AiDeskPage';
import SmartMoneyPage from './pages/SmartMoneyPage';
import ChartsPage from './pages/ChartsPage';
import BacktestPage from './pages/BacktestPage';
import AnalyticsPage from './pages/AnalyticsPage';
import WatchlistPage from './pages/WatchlistPage';
import AppGuidePage from './pages/AppGuidePage';
import E2ETestsPage from './pages/E2ETestsPage';
import { useWebSocket } from './hooks/useWebSocket';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// WebSocket Wrapper
const WebSocketWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useSelector((state: RootState) => state.auth);
  useWebSocket(token);
  return <>{children}</>;
};

function AppContent() {
  const themeMode = useSelector((state: RootState) => state.auth.themeMode);
  const theme = themeMode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster
        position="top-left"
        toastOptions={{
          duration: 4000,
          style: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary
          }
        }}
        containerStyle={{ top: 64 }}
      />
      <WebSocketWrapper>
        <ConfirmProvider>
        <InstallPrompt />
        <PwaUpdatePrompt />
        <CookieConsentBanner />
        <Router>
          <TabsProvider>
          <OnboardingTour />
          {/* CL1-CL3 / F4 — Mounts here (inside Router so it can use
              RouterLink to deep-link into the App Guide). Renders nothing
              for users who've already accepted the current legal version. */}
          <LegalAcceptanceGate />
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />

            {/* Zerodha OAuth callback — requires auth but no Layout shell */}
            <Route path="/zerodha-callback" element={
              <ProtectedRoute>
                <ZerodhaCallbackPage />
              </ProtectedRoute>
            } />

            {/* R81 — App Guide. Lives outside the protected Layout shell so
                it opens in its own browser tab as a standalone document. */}
            <Route path="/app-guide" element={
              <ProtectedRoute>
                <AppGuidePage />
              </ProtectedRoute>
            } />
            {/* Redirects from the four legacy guide routes — preserve any
                deep-link bookmarks users may have. */}
            <Route path="/trading-guide"     element={<Navigate to="/app-guide#quick-start"      replace />} />
            <Route path="/guide"             element={<Navigate to="/app-guide#quick-start"      replace />} />
            <Route path="/tradingview-setup" element={<Navigate to="/app-guide#tradingview-setup" replace />} />
            <Route path="/options-guide"     element={<Navigate to="/app-guide#fno-options"       replace />} />
            <Route path="/help"              element={<Navigate to="/app-guide"                   replace />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <Layout />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardLayout />} />
              <Route path="trades" element={<TradesTable />} />

              <Route path="alerts" element={<AlertsList />} />
              <Route path="instruments" element={<InstrumentsPage />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="settings" element={<UserSettings />} />
              <Route path="options" element={<OptionsHubPage />} />
              <Route path="options-dashboard" element={<OptionsDashboardPage />} />
              <Route path="options-lab/:symbol" element={<OptionsLabPage />} />
              {/* Strategies + Options AT merged into one tabbed hub (both routes
                  render it; /option-strategies opens the second tab). */}
              <Route path="strategies" element={<StrategyHubPage />} />
              <Route path="option-strategies" element={<StrategyHubPage />} />
              {/* Golden Signals is the hub's third tab; the alias exists so the
                  guide, the command palette and a bookmark can point at it. */}
              <Route path="golden-signals" element={<StrategyHubPage />} />
              {/* One AI page. The Edge board was judged on its own numbers -- 1,492 resolved
                  picks, direction accuracy 18% against its own 17% baseline, inside the noise --
                  and the parts that earned their place (range bands, per-symbol reliability,
                  model health, the capture/label/retrain pipeline) moved onto the AI Desk. The
                  /edge backend, the corpus and every table are untouched; only the page is gone.
                  Redirected rather than deleted so existing links, bookmarks and the App Guide
                  keep working. */}
              <Route path="edge-ai" element={<Navigate to="/ai-desk?tab=engine" replace />} />
              <Route path="ai-desk" element={<AiDeskPage />} />
              <Route path="smart-money" element={<SmartMoneyPage />} />
              <Route path="charts" element={<ChartsPage />} />
              <Route path="charts/:symbol" element={<ChartsPage />} />
              <Route path="backtest" element={<BacktestPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="watchlist" element={<WatchlistPage />} />
              {/* R117 — Admin-only E2E trade-cycle test harness.
                  Page handles its own admin gating (renders an access-denied
                  banner if /v1/e2e/scenarios returns 403). */}
              <Route path="e2e-tests" element={<E2ETestsPage />} />
            </Route>

            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </TabsProvider>
        </Router>
        </ConfirmProvider>
      </WebSocketWrapper>
    </ThemeProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
